import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function VancouversTopBrunchSpots() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "vancouvers-top-brunch-spots";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Bon’s Off Broadway (Budget-Friendly Legend)",
    "section-2": "Jam Café (Hearty & Trendy All-Day Brunch)",
    "section-3": "Café Medina (Iconic Brunch Bistro)",
    "section-4": "Sophie’s Cosmic Café (Family-Friendly Retro Diner)",
    "section-5": "The Acorn (Vegan-Friendly Brunch Excellence)",
    "section-6": "Botanist (Luxurious Brunch Experience)",
    "section-7": "Ophelia (Trendy Mexican Brunch)",
    etiquette: "Local Brunch Etiquette & Best Practices",
    seasonal: "Seasonal Recommendations",
    faq: "FAQ",
    "local-regulations": "Local Regulations",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://www.destinationvancouver.com/places-to-eat/",
      label: "Destination Vancouver: Food & Drink Guide",
    },
    {
      url: "https://guide.michelin.com/us/en/best-of/vancouver-best-brunch-michelin-guide",
      label: "Michelin Guide – Vancouver Brunch Picks",
    },
    {
      url: "https://www.timeout.com/vancouver/restaurants/best-brunch-in-vancouver",
      label: "Time Out Vancouver: 15 Best Brunch Spots",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  const locations = [
    {
      id: "section-1",
      lat: 49.2625,
      lng: -123.0569,
      popupText: "Bon’s Off Broadway: 2451 Nanaimo St, Vancouver, BC V5N 5E5",
    },
    {
      id: "section-2",
      lat: 49.2815,
      lng: -123.1102,
      popupText: "Jam Café: 556 Beatty St, Vancouver, BC V6B 2L3",
    },
    {
      id: "section-3",
      lat: 49.2806,
      lng: -123.1168,
      popupText: "Café Medina: 780 Richards St, Vancouver, BC V6B 3A4",
    },
    {
      id: "section-4",
      lat: 49.2683,
      lng: -123.1526,
      popupText: "Sophie’s Cosmic Café: 2095 W 4th Ave, Vancouver, BC V6J 1N3",
    },
    {
      id: "section-5",
      lat: 49.2498,
      lng: -123.1013,
      popupText: "The Acorn: 3995 Main St, Vancouver, BC V5V 3P3",
    },
    {
      id: "section-6",
      lat: 49.2881,
      lng: -123.1164,
      popupText: "Botanist: 1038 Canada Place, Vancouver, BC V6C 0B9",
    },
    {
      id: "section-7",
      lat: 49.2690,
      lng: -123.1090,
      popupText: "Ophelia: 165 W 2nd Ave, Vancouver, BC V5Y 1B8",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Vancouver’s Top Brunch Places You Need to Try</title>
        <meta
          name="description"
          content="A comprehensive guide to Vancouver’s top brunch spots, featuring must-try dishes, locations, local etiquette, and seasonal recommendations."
        />
        <meta
          name="keywords"
          content="Vancouver, brunch, restaurants, breakfast, local regulations, where to eat"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/vancouvers-top-brunch-spots.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Vancouver’s Top Brunch Places You Need to Try"
        />
        <meta
          property="og:description"
          content="A thorough guide to Vancouver’s best brunch spots, local etiquette, addresses, coordinates, and more."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/vancouvers-top-brunch-spots.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/vancouvers-top-brunch-spots.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Vancouver’s Top Brunch Places You Need to Try"
        />
        <meta
          name="twitter:description"
          content="Discover Vancouver's best brunch cafes and restaurants, complete with local tips and must-try dishes."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/vancouvers-top-brunch-spots.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="A delicious brunch table spread"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver is a brunch-lover’s paradise, where the city’s
              multicultural food scene and fresh local ingredients come together
              each morning. From decadent upscale hotel buffets to cozy
              neighborhood cafes, the city offers diverse brunch experiences to
              suit every taste. In this guide, we highlight the top brunch
              places you need to try, covering budget-friendly diners,
              luxurious hotels, vegan-friendly restaurants, and more.
            </p>

            {/* SECTION: Bon’s Off Broadway */}
            <h2 id="section-1">Bon’s Off Broadway (Budget-Friendly Legend)</h2>
            <p
              ref={(el) => setSpotRef("section-1", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2625}
                coordX={-123.0569}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              2451 Nanaimo St, Vancouver, BC V5N 5E5
            </p>
            <p>
              <strong>Operating Hours:</strong> 8:00 AM – 7:00 PM (Daily)
              <br />
              <strong>Approximate Cost:</strong> $
            </p>
            <p>
              Bon’s Off Broadway is an iconic diner known for its famous $2.95
              all-day breakfast special. Despite the graffiti-covered walls and
              no-frills ambiance, the place is cherished by locals for its
              friendly vibe. Expect simple but hearty plates of eggs, hash
              browns, toast, and ham. It’s cash-only, so make sure to bring some
              Canadian currency along.
            </p>

            {/* SECTION: Jam Café */}
            <h2 id="section-2">Jam Café (Hearty & Trendy All-Day Brunch)</h2>
            <p
              ref={(el) => setSpotRef("section-2", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2815}
                coordX={-123.1102}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              556 Beatty St, Vancouver, BC V6B 2L3
            </p>
            <p>
              <strong>Operating Hours:</strong> Weekdays 8:00 AM – 2:30 PM;
              Weekends 8:00 AM – 3:00 PM
              <br />
              <strong>Approximate Cost:</strong> $$
            </p>
            <p>
              Jam Café is a crowd favorite, offering comfort-food brunch dishes
              like fried chicken benedict and mountain-high pancakes. The vibe
              is rustic vintage, and the line-ups can be long—so arrive early!
              Generous portions, a lively atmosphere, and friendly staff make it
              worth the wait. Try their Pulled Pork Tater Bowl or Red Velvet
              Pancakes for a sweet treat.
            </p>

            {/* SECTION: Café Medina */}
            <h2 id="section-3">Café Medina (Iconic Brunch Bistro)</h2>
            <p
              ref={(el) => setSpotRef("section-3", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2806}
                coordX={-123.1168}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              780 Richards St, Vancouver, BC V6B 3A4
            </p>
            <p>
              <strong>Operating Hours:</strong> Mon–Fri 8:00 AM – 3:00 PM;
              Weekends/Holidays 9:00 AM – 3:00 PM
              <br />
              <strong>Approximate Cost:</strong> $$
            </p>
            <p>
              Café Medina boasts legendary Liège-style Belgian waffles and
              Mediterranean-inspired brunch dishes such as the famous Fricassée
              (braised short ribs, roasted potatoes, fried eggs, applewood
              cheddar). Expect long lines on weekends, but the wait is rewarded
              with exceptional flavors. Don’t skip the lavender latte or any of
              their gourmet waffle toppings.
            </p>

            {/* SECTION: Sophie’s Cosmic Café */}
            <h2 id="section-4">Sophie’s Cosmic Café (Family-Friendly Retro Diner)</h2>
            <p
              ref={(el) => setSpotRef("section-4", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2683}
                coordX={-123.1526}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              2095 W 4th Ave, Vancouver, BC V6J 1N3
            </p>
            <p>
              <strong>Operating Hours:</strong> Monday 8:00 AM – 2:30 PM;
              Tue–Sun 8:00 AM – 8:00 PM
              <br />
              <strong>Approximate Cost:</strong> $
            </p>
            <p>
              Sophie’s Cosmic Café is a Kitsilano staple with quirky retro decor
              and a massive menu. Great for families and large groups, it serves
              everything from classic omelettes to burgers, milkshakes, and
              hearty brunch platters. Kids will love the colorful interior,
              while grown-ups appreciate the homestyle cooking and fun vibes.
            </p>

            {/* SECTION: The Acorn */}
            <h2 id="section-5">The Acorn (Vegan-Friendly Brunch Excellence)</h2>
            <p
              ref={(el) => setSpotRef("section-5", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2498}
                coordX={-123.1013}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              3995 Main St, Vancouver, BC V5V 3P3
            </p>
            <p>
              <strong>Operating Hours:</strong> Brunch on weekends 10:00 AM –
              2:30 PM
              <br />
              <strong>Approximate Cost:</strong> $$$
            </p>
            <p>
              An award-winning vegetarian eatery, The Acorn elevates plant-based
              cuisine with creative presentations and fresh local produce. Try
              the Southern Fried Oyster Mushrooms on a waffle for a unique twist
              on a classic chicken & waffles dish—no chicken required! The
              atmosphere is cozy yet upscale, perfect for a leisurely weekend
              brunch.
            </p>

            {/* SECTION: Botanist */}
            <h2 id="section-6">Botanist (Luxurious Brunch Experience)</h2>
            <p
              ref={(el) => setSpotRef("section-6", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2881}
                coordX={-123.1164}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              1038 Canada Place, Vancouver, BC V6C 0B9
            </p>
            <p>
              <strong>Operating Hours:</strong> Weekend Brunch 11:30 AM – 2:00
              PM
              <br />
              <strong>Approximate Cost:</strong> $$$$
            </p>
            <p>
              Located inside the Fairmont Pacific Rim, Botanist offers upscale
              brunch with a Pacific Northwest flair. Enjoy gourmet dishes like
              salmon tartare, elegant eggs Benedict, and award-winning cocktails
              surrounded by lush garden-inspired decor. Ideal for special
              occasions or when you want a luxurious weekend treat.
            </p>

            {/* SECTION: Ophelia */}
            <h2 id="section-7">Ophelia (Trendy Mexican Brunch)</h2>
            <p
              ref={(el) => setSpotRef("section-7", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2690}
                coordX={-123.1090}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              165 W 2nd Ave, Vancouver, BC V5Y 1B8
            </p>
            <p>
              <strong>Operating Hours:</strong> Weekend Brunch 10:30 AM – 2:30
              PM
              <br />
              <strong>Approximate Cost:</strong> $$$
            </p>
            <p>
              For a Latin twist, head to Ophelia in Olympic Village. Specializing
              in modern Mexican cuisine, they serve authentic dishes like
              chilaquiles, huevos divorciados, or tres leches French toast. The
              vibrant decor and creative cocktails make it a trendy pick for
              those seeking a brunch experience beyond the usual North American
              staples.
            </p>

            {/* SECTION: Local Brunch Etiquette & Best Practices */}
            <h2 id="etiquette">Local Brunch Etiquette & Best Practices</h2>
            <p>
              Vancouver’s brunch culture is laid-back, but a few tips ensure a
              smooth experience:
            </p>
            <ul>
              <li>
                <strong>Reservations vs. Walk-ins:</strong> Upscale spots and
                hotels often accept reservations; casual diners typically don’t.
                Plan accordingly.
              </li>
              <li>
                <strong>Peak Hours:</strong> Most locals brunch between 10 AM
                and 1 PM on weekends. Go early or opt for a later seating to
                avoid long lines.
              </li>
              <li>
                <strong>Tipping Culture:</strong> 15-20% is standard for table
                service.
              </li>
              <li>
                <strong>Dietary Needs:</strong> Vancouver restaurants are very
                accommodating of vegetarian, vegan, and gluten-free diets.
              </li>
            </ul>

            {/* SECTION: Seasonal Recommendations */}
            <h2 id="seasonal">Seasonal Recommendations</h2>
            <p>
              <strong>Summer Patios:</strong> Many restaurants open their
              terraces in the sunny months. Look for waterfront views at places
              like Dockside (Granville Island) or Lift Bar & Grill (Coal
              Harbour).
            </p>
            <p>
              <strong>Cozy Winter Spots:</strong> Brunch by a fireplace at
              Bacchus (Wedgewood Hotel) or Twisted Fork Bistro. Enjoy warm
              beverages and comfort food when it’s rainy or snowy outside.
            </p>
            <p>
              <strong>Fall Harvest:</strong> Seasonal menus often feature
              pumpkin spice pancakes, apple compotes, and root vegetable hashes.
              Check out farm-to-table spots like Fable Kitchen or Heirloom
              Vegetarian.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Do I need reservations for brunch?</h3>
              <p>
                Many popular brunch spots in Vancouver don’t take reservations
                (e.g., Jam Café, Café Medina), so arrive early. Upscale
                restaurants and hotels do accept bookings—reserve ahead if
                possible.
              </p>

              <h3>2. What is the average cost of brunch in Vancouver?</h3>
              <p>
                Casual diners range around $10–$15 per person; mid-range brunch
                cafes $15–$25; upscale hotel brunches can go $40+ per person.
              </p>

              <h3>3. Are these places family-friendly?</h3>
              <p>
                Absolutely. Most Vancouver brunch spots are family-friendly,
                especially places like Sophie’s Cosmic Café and hotel buffets
                with diverse menu options.
              </p>
            </div>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations</h2>
            <p>
              Vancouver’s health and safety regulations can change, so always
              check official updates from local authorities. Some restaurants
              might have specific reservation or capacity rules during holiday
              seasons or special events. If you have severe allergies, inform
              staff in advance to ensure a safe dining experience.
            </p>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Whether you’re craving a budget-friendly classic breakfast, a
              lavish brunch with a champagne toast, or a vegan masterpiece,
              Vancouver has a spot for you. Embrace the city’s vibrant brunch
              culture, explore different neighborhoods, and taste what each
              unique establishment has to offer. With so many choices, you can
              enjoy a new brunch experience every weekend—and savor the best of
              Vancouver’s culinary scene.
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}